import clsx from 'clsx';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { ComponentSize, MeetingState, PracticeProspect } from '../../types';
import { ManageMicAccess, Spinner } from '../shared';
import DialerFeedbackModal from './DialerFeedbackModal';

interface DialerWrapperProps {
  children: React.ReactNode;
  currProspect: PracticeProspect;
  meetingState: MeetingState;
  isMicAccessGranted: boolean;
  handleClose: () => void;
  runStartCall: (newIsRedialing: boolean) => void;
  setIsMicAccessGranted: Dispatch<SetStateAction<boolean>>;
  setProspects: Dispatch<SetStateAction<PracticeProspect[]>>;
  stopCall: () => void;
  callSid?: string;
  isLoading?: boolean;
}

const DialerWrapper = ({
  children,
  currProspect,
  meetingState,
  isMicAccessGranted,
  handleClose,
  runStartCall,
  setIsMicAccessGranted,
  setProspects,
  stopCall,
  callSid,
  isLoading,
}: DialerWrapperProps) => {
  const { personaId } = currProspect;

  // Start a new call whenever the prospect changes.
  useEffect(() => {
    if (meetingState === MeetingState.IDLE) {
      runStartCall(false);
    }
  }, [runStartCall]);

  // Update the prospect used state after ending a successful call.
  // The call is successful if it has ended and the callSid is defined.
  useEffect(() => {
    if (callSid && meetingState === MeetingState.IDLE) {
      setProspects((prev) =>
        prev.map((p) => (p.personaId === personaId ? { ...p, isUsed: true, lastCall: { endTime: new Date() } } : p))
      );
    }
  }, [callSid, meetingState, personaId, setProspects]);

  return (
    <>
      <ManageMicAccess onCancel={handleClose} onDenied={stopCall} setIsMicAccessGranted={setIsMicAccessGranted} />
      {isMicAccessGranted && (
        <div className="fixed inset-0 z-50 flex h-full items-end">
          {/* Overlay - Only allow clicking the overlay to close the modal if the call is not in progress. */}
          <div
            className="bg-base-content absolute inset-0 bg-opacity-40"
            onClick={meetingState === MeetingState.IDLE ? handleClose : undefined}
          />
          <div className="border-base-300 relative z-50 flex h-36 w-full items-center justify-center border-t bg-base-150 px-8 shadow-dialog">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner size={ComponentSize.MEDIUM} />
              </div>
            )}
            <div className={clsx('w-full', isLoading && 'invisible')}>{children}</div>
          </div>
          {meetingState === MeetingState.IDLE && callSid && <DialerFeedbackModal callSid={callSid} />}
        </div>
      )}
    </>
  );
};

export default DialerWrapper;
